import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Target, Eye, EyeOff, ArrowRight, Sparkles, Users } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { login, error } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalError, setLegalError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLegalError(null);
    if (!legalAccepted) {
      setLegalError('You must agree to the Terms of Use & Risk Disclosure to continue.');
      return;
    }
    setIsLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-dark-900 flex items-start sm:items-center justify-center px-4 pt-20 pb-10 sm:p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-accent-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary-600/5 to-transparent" />
      </div>
      
      {/* Top header with About / Blog links */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3 text-xs">
        <div className="font-semibold text-sm text-gradient">BetMimic</div>
        <div className="flex items-center gap-4">
          <Link
            to="/about"
            className="text-dark-400 hover:text-primary-300 transition-colors"
          >
            About
          </Link>
          <Link
            to="/blog"
            className="text-dark-400 hover:text-primary-300 transition-colors"
          >
            Blog
          </Link>
        </div>
      </div>

      <div className="w-full max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          <section className="order-2 lg:order-1 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-dark-700/70 bg-dark-800/60 text-xs text-dark-300">
              <Sparkles className="w-4 h-4 text-primary-400" />
              <span className="font-medium">Non-custodial Polymarket copy trading</span>
            </div>

            {/* Logo */}
            <div className="mt-6">
              <div className="inline-flex items-center justify-center mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl rotate-6 opacity-60 blur-md scale-110" />
                <div className="relative w-20 h-20 bg-dark-900/20 rounded-2xl flex items-center justify-center shadow-2xl shadow-primary-500/30 overflow-hidden">
                  <img
                    src="/BETMIMICLOGO.jpg"
                    alt="BetMimic"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                <span className="text-gradient">BetMimic</span>
              </h1>
              <p className="text-dark-300 text-lg md:text-xl mt-3 max-w-xl">
                Copy the best Polymarket traders with a clean execution layer, real fill tracking, and risk controls that
                keep you alive.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-dark-700/70 bg-dark-800/60 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-dark-500">Execution</p>
                <p className="mt-2 text-sm text-dark-200">
                  Market-like and limit order routing through the official CLOB, with timeouts and guardrails.
                </p>
              </div>
              <div className="rounded-2xl border border-dark-700/70 bg-dark-800/60 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-dark-500">Monitoring</p>
                <p className="mt-2 text-sm text-dark-200">
                  Real-time tracking, positions, and PnL so you can verify fills and spot drift fast.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-dark-400">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dark-800/60 border border-dark-700/70">
                <Sparkles className="w-4 h-4 text-primary-400" />
                Auto copy trades
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dark-800/60 border border-dark-700/70">
                <Target className="w-4 h-4 text-accent-400" />
                Real-time tracking
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dark-800/60 border border-dark-700/70">
                <Users className="w-4 h-4 text-primary-400" />
                Community support
              </span>
            </div>
          </section>

          <section className="order-1 lg:order-2">
            {/* Form */}
            <form onSubmit={handleSubmit} className="card card-glow space-y-5 animate-slide-up">
              <div className="text-center pb-2">
                <h2 className="text-xl font-semibold">Welcome back</h2>
                <p className="text-dark-500 text-sm mt-1">Sign in to continue trading</p>
              </div>

              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 text-sm">
                <div className="font-semibold">High-risk trading warning</div>
                <div className="mt-1 text-red-200/90">
                  Prediction markets and automation can lead to rapid losses. BetMimic is not a broker, exchange, custodian,
                  or investment adviser. You are solely responsible for your trades.
                </div>
                <div className="mt-3 flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={legalAccepted}
                    onChange={(e) => setLegalAccepted(e.target.checked)}
                  />
                  <div className="text-sm">
                    I agree to the{' '}
                    <Link to="/legal" className="text-primary-300 hover:text-primary-200 underline">
                      Terms of Use & Risk Disclosure
                    </Link>
                    .
                  </div>
                </div>
                {legalError ? <div className="mt-2 text-red-300">{legalError}</div> : null}
              </div>
              
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  {error}
                </div>
              )}

              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="label">Password</label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-12"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-gradient w-full py-3.5 text-base group"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign in
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </button>
            </form>

            <p className="text-center text-dark-500 mt-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-400 hover:text-primary-300 font-medium">
                Create one free
              </Link>
            </p>

            {/* Blog links */}
            <div
              className="mt-6 text-center text-dark-400 text-xs sm:text-sm space-y-2 animate-fade-in"
              style={{ animationDelay: '0.6s' }}
            >
              <p className="uppercase tracking-wide text-[0.7rem] text-dark-500">
                Learn how Polymarket copy trading works
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link
                  to="/blog/polymarket-copy-trading"
                  className="px-3 py-1 rounded-full border border-dark-600/80 bg-dark-800/70 hover:border-primary-500 hover:text-primary-200 text-[0.7rem] sm:text-xs transition-colors"
                >
                  Polymarket copy trading guide
                </Link>
                <Link
                  to="/blog/polymarket-risk-management"
                  className="px-3 py-1 rounded-full border border-dark-600/80 bg-dark-800/70 hover:border-emerald-500 hover:text-emerald-200 text-[0.7rem] sm:text-xs transition-colors"
                >
                  Risk management on Polymarket
                </Link>
                <Link
                  to="/blog/polymarket-copy-trading-strategies"
                  className="px-3 py-1 rounded-full border border-dark-600/80 bg-dark-800/70 hover:border-primary-500 hover:text-primary-200 text-[0.7rem] sm:text-xs transition-colors"
                >
                  Copy trading strategies
                </Link>
              </div>
              <p className="text-[0.7rem] text-dark-500">
                Prefer to browse everything?{' '}
                <Link to="/blog" className="text-primary-400 hover:text-primary-300 font-medium">
                  View all blog posts
                </Link>
              </p>
            </div>

            {/* Discord social CTA */}
            <div className="mt-4 flex justify-center animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <a
                href="https://discord.gg/dFSefc9Hs9"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800/80 border border-dark-600 text-sm text-dark-200 hover:border-primary-500 hover:text-white transition-colors"
              >
                <Users className="w-4 h-4 text-primary-400" />
                <span>Join the BetMimic Discord</span>
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
